// Action Types
const SHOW_EDIT_MODAL = 'productVersion/SHOW_EDIT_MODAL';
const HIDE_MODAL = 'productVersion/HIDE_MODAL';

// Import action types from super-client
const SUPER_ADMIN_PRODUCT_VERSIONS_FETCH_SUCCESS = 'SUPER_ADMIN_PRODUCT_VERSIONS_FETCH_SUCCESS';
const SUPER_ADMIN_IMPORT_WHITELIST_CSV = 'SUPER_ADMIN_IMPORT_WHITELIST_CSV';
const SUPER_ADMIN_IMPORT_WHITELIST_CSV_PROGRESS = 'SUPER_ADMIN_IMPORT_WHITELIST_CSV_PROGRESS';
const SUPER_ADMIN_IMPORT_WHITELIST_CSV_SUCCESS = 'SUPER_ADMIN_IMPORT_WHITELIST_CSV_SUCCESS';
const SUPER_ADMIN_IMPORT_WHITELIST_CSV_FAILURE = 'SUPER_ADMIN_IMPORT_WHITELIST_CSV_FAILURE';

// Minimal shape of a product version record (only what is stored/accessed here).
type ProductVersion = Record<string, unknown>;

interface ShowEditModalAction {
  type: typeof SHOW_EDIT_MODAL;
  payload: { productVersion: ProductVersion | null };
}

interface HideModalAction {
  type: typeof HIDE_MODAL;
}

// Action Creators
export const ProductVersionActions = {
  showEditModal: (productVersion: ProductVersion | null): ShowEditModalAction => ({
    type: SHOW_EDIT_MODAL,
    payload: { productVersion },
  }),
  hideModal: (): HideModalAction => ({
    type: HIDE_MODAL,
  }),
};

interface ImportState {
  inProgress: boolean;
  current: number;
  total: number;
  message: string | null;
  error: string | null;
  rowIndex: number | null;
}

interface ModalState {
  isOpen: boolean;
  productVersion: ProductVersion | null;
}

interface ProductVersionState {
  productVersions: ProductVersion[];
  modal: ModalState;
  import: ImportState;
}

// Actions dispatched by the super-client that this reducer also handles.
// These carry extra fields beyond `type`, so the incoming action is typed loosely.
interface ProductVersionAction {
  type: string;
  payload?: { productVersion: ProductVersion | null };
  products?: ProductVersion[];
  data?: unknown[];
  current?: number;
  total?: number;
  message?: string | null;
  error?: string | null;
  rowIndex?: number | null;
}

// Initial State
const initialState: ProductVersionState = {
  productVersions: [],
  modal: {
    isOpen: false,
    productVersion: null,
  },
  import: {
    inProgress: false,
    current: 0,
    total: 0,
    message: null,
    error: null,
    rowIndex: null,
  },
};

// Reducer
export const productVersionReducer = (
  state: ProductVersionState = initialState,
  action: ProductVersionAction,
): ProductVersionState => {
  switch (action.type) {
    case SUPER_ADMIN_PRODUCT_VERSIONS_FETCH_SUCCESS:
      return {
        ...state,
        productVersions: action.products || [],
      };
    case SHOW_EDIT_MODAL:
      return {
        ...state,
        modal: {
          isOpen: true,
          // These action shapes are guaranteed by the dispatching code; `!` is compile-time only.
          productVersion: action.payload!.productVersion,
        },
      };
    case SUPER_ADMIN_IMPORT_WHITELIST_CSV:
      return {
        ...state,
        import: {
          inProgress: true,
          current: 0,
          total: action.data!.length,
          message: 'Starting import...',
          error: null,
          rowIndex: null,
        },
      };
    case SUPER_ADMIN_IMPORT_WHITELIST_CSV_PROGRESS:
      return {
        ...state,
        import: {
          ...state.import,
          current: action.current!,
          total: action.total!,
          message: action.message!,
        },
      };
    case SUPER_ADMIN_IMPORT_WHITELIST_CSV_SUCCESS:
      return {
        ...state,
        import: {
          inProgress: false,
          current: 0,
          total: 0,
          message: 'Import completed successfully!',
          error: null,
          rowIndex: null,
        },
      };
    case SUPER_ADMIN_IMPORT_WHITELIST_CSV_FAILURE:
      return {
        ...state,
        import: {
          ...state.import,
          inProgress: false,
          error: action.error!,
          rowIndex: action.rowIndex!,
          message: action.rowIndex ? `Import failed at row ${action.rowIndex}` : 'Import failed',
        },
      };
    case HIDE_MODAL:
      return {
        ...state,
        modal: {
          isOpen: false,
          productVersion: null,
        },
      };
    default:
      return state;
  }
};
